import React from 'react'
import styled, { keyframes } from 'styled-components'
import useTimer from '@jarvis.oddle/core/hooks/useTimer'
import usePrevious from '@jarvis.oddle/core/hooks/usePrevious'
import * as bodyScrolling from '@jarvis.oddle/core/tools/bodyScrolling'
import Flex from '@jarvis.oddle/core/components/Flex'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { colorConstants } from '@jarvis.oddle/core/theme/default'
import Loading from '@jarvis.oddle/core/components/Loading'
import { createErrorString } from '@jarvis.oddle/core/tools/error'
import IconErrorCircle from '@jarvis.oddle/core/components/Icons/IconErrorCircle'
import { H3, LinkText, P } from '@jarvis.oddle/core/components/Typo'

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const backdropFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 0.7;
  }
`

const backdropFadeOut = keyframes`
  from {
    opacity: 0.7;
  }
  to {
    opacity: 0;
  }
`

const Wrapper = styled(Flex)`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  pointer-events: none;
`

const Overlay = styled(Flex)`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  animation-fill-mode: forwards;
  animation-timing-function: ease-in-out;
  animation-duration: ${(props) => props.transitionDuration}ms;
  pointer-events: ${(props) => (props.show ? 'all' : 'none')};
`

const Backdrop = styled(Overlay)`
  display: ${(props) => (props.show ? 'flex' : 'none')};
  animation-name: ${(props) => (props.show ? backdropFadeIn : backdropFadeOut)};
  background-color: black;
`

const Container = styled(Overlay).attrs(() => ({
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center'
}))`
  animation-name: ${(props) => (props.show ? fadeIn : fadeOut)};

  & > * {
    pointer-events: ${(props) => (props.show ? 'all' : 'none')};
  }
`

interface PropsType {
  initialShow: boolean
  paymentProcessState: PaymentProcess.PaymentProcess
}

const PaymentProcessOverlay = React.forwardRef(
  (
    { initialShow, paymentProcessState }: PropsType,
    ref: React.Ref<{ setShouldShow? }>
  ): JSX.Element => {
    const { t } = React.useContext(TranslationContext)
    const [shouldShow, setShouldShow] = React.useState(initialShow)
    const [transitionDuration, setTransitionDuration] = React.useState(
      initialShow ? 0 : 300
    )
    const durationInMs = 5000
    const { action, paymentCharges } = paymentProcessState

    const prevAction = usePrevious(action)
    const { isRunning, timeLeft, start, reset } = useTimer(durationInMs)

    const onCloseLinkTextClick = React.useCallback(() => {
      setShouldShow(false)
    }, [])

    const { Element, title, subtitle } = React.useMemo(() => {
      let ElementData = null
      let titleString = null
      let subtitleString = null

      switch (action) {
        case 'createPaymentTokenAction.pending':
          ElementData = (
            <Loading color={colorConstants.white} height={100} width={100} />
          )
          titleString = t('Initiating payment process...')
          subtitleString = (
            <P normalised color={colorConstants.white} textAlign='center'>
              {t('Please do not exit this page.')}
            </P>
          )
          break
        case 'createPaymentTokenAction.fulfilled':
          ElementData = (
            <Loading color={colorConstants.white} height={100} width={100} />
          )
          titleString = t('Processing payment request...')
          subtitleString = (
            <P normalised color={colorConstants.white} textAlign='center'>
              {t('Please hold on as we are processing your payment request.')}
            </P>
          )
          break
        case 'createPaymentTokenAction.rejected':
          ElementData = (
            <IconErrorCircle
              height={120}
              width={120}
              fill={colorConstants.white}
            />
          )
          titleString = t(
            'We are sorry, there was an error processing your payment. Please try again with a different payment card.'
          )
          subtitleString = (
            <Flex
              flexDirection='column'
              justifyContent='center'
              alignItems='center'
            >
              {timeLeft > 0 && isRunning && (
                <P normalised color={colorConstants.white} textAlign='center'>
                  {t('Please try again in _timeLeft_ second(s).', {
                    timeLeft: timeLeft / 1000
                  })}
                </P>
              )}
              <LinkText
                onClick={onCloseLinkTextClick}
                color={colorConstants.white}
                textAlign='center'
              >
                {t('Let me try again now')}
              </LinkText>
            </Flex>
          )
          break
        case 'postStripeChargesAction.pending':
          ElementData = (
            <Loading color={colorConstants.white} height={100} width={100} />
          )
          titleString = t('Processing payment...')
          subtitleString = (
            <P normalised color={colorConstants.white} textAlign='center'>
              {t('Your payment is under processing now. Please hold tight.')}
            </P>
          )
          break
        case 'postStripeChargesAction.fulfilled':
          ElementData = (
            <Loading color={colorConstants.white} height={100} width={100} />
          )
          titleString = t('Payment has been made.')
          subtitleString = (
            <P normalised color={colorConstants.white} textAlign='center'>
              {t(
                'Please hold on as your order is being submitted to us. Do not exit this page.'
              )}
            </P>
          )
          break
        case 'postStripeChargesAction.rejected':
          const errorMessage = createErrorString({
            t,
            error: paymentCharges.error
          }).join(' ')
          ElementData = (
            <IconErrorCircle
              height={120}
              width={120}
              fill={colorConstants.white}
            />
          )
          titleString = t(
            'We are sorry, there was an error processing your payment.'
          )
          subtitleString = (
            <Flex
              flexDirection='column'
              justifyContent='center'
              alignItems='center'
            >
              <P normalised color={colorConstants.white} textAlign='center'>
                {errorMessage}
              </P>
              {timeLeft > 0 && isRunning && (
                <P color={colorConstants.white} textAlign='center'>
                  {t('Please try again in _timeLeft_ second(s).', {
                    timeLeft: timeLeft / 1000
                  })}
                </P>
              )}
              <LinkText
                onClick={onCloseLinkTextClick}
                color={colorConstants.white}
                textAlign='center'
              >
                {t('Let me try again now')}
              </LinkText>
            </Flex>
          )
          break
        default:
          ElementData = (
            <Loading color={colorConstants.white} height={100} width={100} />
          )
          titleString = t('Processing...')
          subtitleString = null
          break
      }

      return {
        Element: ElementData,
        title: titleString,
        subtitle: subtitleString
      }
    }, [
      action,
      isRunning,
      onCloseLinkTextClick,
      paymentCharges.error,
      t,
      timeLeft
    ])

    React.useImperativeHandle(
      ref,
      () => ({
        setShouldShow: (s) => setShouldShow(s)
      }),
      []
    )

    React.useEffect(() => {
      if (action !== prevAction && (action || '').indexOf('pending') > -1) {
        setShouldShow(true)
      }
    }, [action, prevAction])

    React.useEffect(() => {
      if (action !== prevAction && (action || '').indexOf('fulfilled') > -1) {
        setShouldShow(true)
        setTransitionDuration(300)
      }
    }, [action, prevAction])

    React.useEffect(() => {
      if (action !== prevAction && (action || '').indexOf('rejected') > -1) {
        reset()
        start()
        setTransitionDuration(300)
        setTimeout(() => setShouldShow(false), durationInMs)
      }
    }, [action, durationInMs, prevAction, reset, start])

    React.useEffect(() => {
      if (shouldShow) {
        bodyScrolling.lock('overlay')
      } else {
        bodyScrolling.release('overlay')
      }

      return () => bodyScrolling.release('overlay')
    }, [shouldShow])

    return (
      <Wrapper>
        <Backdrop show={shouldShow} transitionDuration={transitionDuration} />
        <Container show={shouldShow} transitionDuration={transitionDuration}>
          <Flex
            width='40%'
            flexDirection='column'
            justifyContent='center'
            alignItems='center'
          >
            {Element}
            {title && (
              <H3 color={colorConstants.white} textAlign='center'>
                {title}
              </H3>
            )}
            {subtitle}
          </Flex>
        </Container>
      </Wrapper>
    )
  }
)

export default PaymentProcessOverlay
